/**
 * dmPreview.ts — the resume object the Edit Resume panel draws for a
 * ROLE-TARGETED ("dedicated master") preview source.
 *
 * Owner design, 2026-08-16: a preview is for EYEBALLING STRUCTURE AND LAYOUT.
 * So the two halves come from two different places and never mix:
 *
 *   CONTENT  is FROZEN from that role-targeted resume's last generation —
 *            summary, highlights, skills, experience bullets, listings.
 *            Lengths matter (that is what you are eyeballing), wording does
 *            not. Typing in the editor must NOT move a role-targeted preview;
 *            only hitting Regenerate on that row refreshes this content.
 *   STRUCTURE is LIVE from the editor — name, contact, section order, section
 *            renames, omit toggles, skill groups and the skills display
 *            settings. Those are the things a user is changing while they
 *            watch the panel, so they must land immediately.
 *
 * Nothing in here triggers a generation; it is a pure reshape of data already
 * fetched. The cache at the bottom keeps that fetch to once per role-targeted
 * resume — its content only changes on Regenerate, which invalidates it.
 */
import type {
  DedicatedMasterContent, DedicatedMasterContentCompany, DedicatedMasterContentItem,
  ExperienceBullet, ExperienceCompany, ExperienceRole, ResumeJSON, Skill,
} from '../types';

/** Generated content carries no include flags — it IS the included set. */
const ON = '1';

const key = (s: string | null | undefined): string => (s || '').trim().toLowerCase();

/** Flat listing sections carried over verbatim from the generated resume. */
const LIST_SECTIONS = [
  'education', 'awards', 'presentations', 'publications', 'portfolios',
] as const;

function mapItems(items: DedicatedMasterContentItem[] | undefined): Record<string, unknown>[] {
  return (items || []).map(it => ({ ...it, include: ON }));
}

/**
 * STRUCTURE-WINS filter, at the COMPANY and ROLE level only.
 *
 * Unchecking a company or a role in the editor is a STRUCTURAL statement ("this
 * job is off my resume"), so it must hide that block in a role-targeted preview
 * too — otherwise the panel shows a company the next regenerate will drop.
 *
 * Matching is by case-insensitive company name / role title, because the frozen
 * content has no ids back to the editor's rows.
 *
 * There is deliberately NO bullet-level matching: generated bullets are
 * REWRITTEN from the resume's originals, so a text match would be fuzzy and
 * would silently drop (or keep) the wrong lines. Bullet-level include changes
 * therefore show up on the next Regenerate, not in the preview.
 *
 * A company (or role) with no live counterpart — a renamed employer, content
 * frozen before an edit — passes through untouched rather than vanishing.
 */
function filterByLiveStructure(
  dmCompanies: DedicatedMasterContentCompany[],
  liveCompanies: ExperienceCompany[] | undefined,
): DedicatedMasterContentCompany[] {
  const live = new Map<string, ExperienceCompany>();
  for (const c of liveCompanies || []) {
    const k = key(c?.company);
    if (k && !live.has(k)) live.set(k, c);
  }
  const out: DedicatedMasterContentCompany[] = [];
  for (const c of dmCompanies || []) {
    const match = live.get(key(c?.company));
    if (match && match.include === '0') continue;          // company switched off
    if (!match) { out.push(c); continue; }                 // no counterpart: keep
    const liveRoles = new Map<string, ExperienceRole>();
    for (const r of match.roles || []) {
      const k = key(r?.title);
      if (k && !liveRoles.has(k)) liveRoles.set(k, r);
    }
    out.push({
      ...c,
      roles: (c.roles || []).filter(r => {
        const lr = liveRoles.get(key(r?.title));
        return !lr || lr.include !== '0';                  // role switched off
      }),
    });
  }
  return out;
}

/**
 * The object the panel renders for a role-targeted source: live structure
 * wearing frozen content. Never mutates either input.
 */
export function mergeDmPreviewResume(
  liveResume: ResumeJSON,
  dm: DedicatedMasterContent,
): ResumeJSON {
  const skills: Skill[] = (dm.skills || []).map(s => ({
    title: s?.title || '',
    // The generated resume carries no per-skill tags; the panel never reads
    // them, it groups by group_id against the LIVE skill_groups.
    tags: [],
    group_id: s?.group_id ?? null,
    include: ON,
  }));

  const experience: ExperienceCompany[] =
    filterByLiveStructure(dm.experience || [], liveResume.experience).map(c => ({
      company: c.company || '',
      url: [],
      startDate: c.startDate || '',
      endDate: c.endDate || '',
      current: !!c.current,
      description: c.description,
      include: ON,
      roles: (c.roles || []).map((r): ExperienceRole => ({
        title: r.title || '',
        startDate: r.startDate || '',
        endDate: r.endDate || '',
        current: !!r.current,
        include: ON,
        bullets: (r.bullets || []).map((b): ExperienceBullet => ({
          text: b?.text || '',
          tags: [],
          include: ON,
        })),
      })),
    }));

  const merged: ResumeJSON = {
    // Structure lives: name, contact, groups and every display setting stay
    // with the editor, so renames/reorders/omits move the preview instantly.
    ...liveResume,
    // Content is frozen.
    summary: dm.summary || '',
    highlights: (dm.highlights || []).filter(Boolean),
    skills,
    experience,
  };
  for (const section of LIST_SECTIONS) {
    // Same shape as the editor's listing items plus an include flag; the exact
    // per-section types differ only in optional fields the panel ignores.
    (merged as unknown as Record<string, unknown>)[section] =
      mapItems(dm[section] as DedicatedMasterContentItem[] | undefined);
  }
  return merged;
}

/** "Aug 14, 2026", or null when there is no usable timestamp. */
export function formatGeneratedAt(at: string | null | undefined): string | null {
  if (!at) return null;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ------------------------------------------------------------- the cache ----
// Frozen content only changes when the user regenerates that role-targeted
// resume, so it is cached module-wide (survives closing and reopening the
// panel) and dropped explicitly by the editor's Regenerate handler.

const cache = new Map<string, DedicatedMasterContent | null>();

export function dmContentCacheKey(
  resumename: string | null | undefined, path: string | null | undefined,
): string {
  return `${resumename || '__new__'}::${(path || '').toLowerCase()}`;
}

export function getCachedDmContent(
  resumename: string | null | undefined, path: string | null | undefined,
): DedicatedMasterContent | null | undefined {
  return cache.get(dmContentCacheKey(resumename, path));
}

export function setCachedDmContent(
  resumename: string | null | undefined,
  path: string | null | undefined,
  content: DedicatedMasterContent | null,
): void {
  cache.set(dmContentCacheKey(resumename, path), content);
}

/** Called when that role-targeted resume is regenerated — next open refetches. */
export function clearCachedDmContent(
  resumename: string | null | undefined, path: string | null | undefined,
): void {
  cache.delete(dmContentCacheKey(resumename, path));
}
